<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import ImageResultPreview from '@/components/Tools/ImageResultPreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolImageCheckboxRow from '@/components/Tools/ToolImageCheckboxRow.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import ToolSliderField from '@/components/Tools/ToolSliderField.vue';
import { Button } from '@/components/ui/button';
import { triggerBlobDownload } from '@/composables/useBlobDownload';
import {
    extractImageToolError,
    postImageToolForm,
} from '@/composables/useImagePost';
import { useImageUploadLimit } from '@/composables/useImageUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { HEIC_ACCEPT } from '@/data/heicToolAccept';
import { dashboard } from '@/routes';
import { heic } from '@/routes/tools/image';
import heicSubmit from '@/routes/tools/image/heic';

const { lineUpTo } = useImageUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.image.heic.seo',
    fallbackTitleKey: 'tools.image.heic.title',
    fallbackTitleLabel: 'HEIC to JPG',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: heic.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.image.heic.title',
                title: 'HEIC to JPG',
                href: heic.url(),
            },
        ],
    },
});

type TargetFormat = 'jpeg' | 'webp' | 'png';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const targetFormat = ref<TargetFormat>('jpeg');
const quality = ref(90);
const stripMetadata = ref(true);
const status = ref<Status>('idle');
const statusMessage = ref('');
const resultBlob = ref<Blob | null>(null);
const resultFilename = ref('');

watch(
    [() => targetFormat.value, () => quality.value, () => stripMetadata.value],
    () => {
        if (!resultBlob.value) {
            return;
        }

        resultBlob.value = null;
        resultFilename.value = '';

        if (status.value === 'success') {
            status.value = 'idle';
            statusMessage.value = '';
        }
    },
);

async function runConvert(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.image.ui.processing', 'Processing…');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('target_format', targetFormat.value);
    formData.append('quality', String(quality.value));
    formData.append('strip_metadata', stripMetadata.value ? '1' : '0');

    try {
        const { blob, filename } = await postImageToolForm(
            heicSubmit.submit.url(),
            formData,
        );
        resultBlob.value = blob;
        resultFilename.value = filename;
        status.value = 'success';
        statusMessage.value = t(
            'tools.image.ui.result_preview_ready_status',
            'Preview ready. Use the Download button above to save your file.',
        );
    } catch (err: unknown) {
        status.value = 'error';
        let msg = await extractImageToolError(err);

        if (/heic|heif|imagick/i.test(msg)) {
            msg = t(
                'tools.image.ui.heic_unavailable',
                'HEIC conversion is not available on this server. Imagick with libheif support is required.',
            );
        }

        statusMessage.value = msg;
    }
}

function onPrimaryAction(): void {
    if (resultBlob.value && resultFilename.value) {
        triggerBlobDownload(resultBlob.value, resultFilename.value);

        return;
    }

    void runConvert();
}

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
    resultBlob.value = null;
    resultFilename.value = '';
}

function onRemoveFile(): void {
    file.value = null;
    status.value = 'idle';
    resultBlob.value = null;
    resultFilename.value = '';
}

function reset(): void {
    resultBlob.value = null;
    resultFilename.value = '';
    file.value = null;
    status.value = 'idle';
    statusMessage.value = '';
}

onUnmounted(() => {
    resultBlob.value = null;
});
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-image-heic" />

    <ToolLayout
        :title="t('tools.image.heic.title', '')"
        :subtitle="t('tools.image.heic.description', '')"
        how-to-placement="below_preview"
        preview-track="wide"
    >
        <template #preview>
            <div
                class="flex min-h-[min(50vh,22rem)] items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/20 p-3 shadow-inner"
            >
                <ImageResultPreview
                    v-if="resultBlob && file"
                    class="w-full max-w-full"
                    :original-file="file"
                    :result-blob="resultBlob"
                    :result-filename="resultFilename"
                    :comparison="false"
                    hide-actions
                />
                <p
                    v-else-if="file"
                    class="px-4 text-center text-sm text-muted-foreground"
                >
                    {{
                        t(
                            'tools.image.ui.heic_preview_pending',
                            'HEIC files cannot be previewed in the browser. Convert to see the result.',
                        )
                    }}
                </p>
                <p
                    v-else
                    class="px-4 text-center text-sm text-muted-foreground"
                >
                    {{
                        t(
                            'tools.image.ui.heic_preview_empty',
                            'Upload a HEIC or HEIF photo from your iPhone or camera.',
                        )
                    }}
                </p>
            </div>
        </template>

        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    :accept="HEIC_ACCEPT"
                    :label="
                        t(
                            'tools.image.ui.heic_drop_label',
                            'Click or drag HEIC here',
                        )
                    "
                    :sublabel="
                        lineUpTo(
                            t(
                                'tools.image.ui.heic_files_only',
                                'HEIC or HEIF only',
                            ),
                        )
                    "
                    :preview-file="file"
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard :file="file" @remove="onRemoveFile" />
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <p class="mb-2 text-sm font-medium">
                    {{ t('tools.image.ui.target_format', '') }}
                </p>
                <select
                    v-model="targetFormat"
                    class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                    <option value="jpeg">JPEG</option>
                    <option value="png">PNG</option>
                    <option value="webp">WebP</option>
                </select>
                <div v-if="targetFormat !== 'png'" class="mt-4">
                    <ToolSliderField
                        v-model="quality"
                        :min="1"
                        :max="100"
                        :step="1"
                        :label="t('tools.image.ui.quality', '')"
                    />
                </div>
                <details class="mt-4">
                    <summary
                        class="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        {{
                            t(
                                'tools.image.ui.advanced_options',
                                'Advanced options',
                            )
                        }}
                    </summary>
                    <div class="mt-2">
                        <ToolImageCheckboxRow
                            v-model="stripMetadata"
                            :label="t('tools.image.ui.strip_metadata', '')"
                        />
                    </div>
                </details>
            </div>

            <Button
                type="button"
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="onPrimaryAction"
            >
                <span v-if="status === 'loading'">{{
                    t('tools.image.ui.processing', 'Processing…')
                }}</span>
                <span v-else-if="resultBlob && file">{{
                    t('tools.image.ui.download_result', 'Download')
                }}</span>
                <span v-else>{{
                    t('tools.image.ui.heic_convert_submit', 'Convert HEIC')
                }}</span>
            </Button>
            <StatusMessage :status="status" :message="statusMessage" />
            <Button
                v-if="resultBlob && file"
                type="button"
                variant="outline"
                class="w-full"
                @click="reset"
            >
                {{ t('tools.image.ui.process_another', 'Process another') }}
            </Button>
        </div>
    </ToolLayout>
</template>
